import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { fmtDate, STATUS, FLAGS } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel } from "@/pages/shared";
import { LABEL_W, STAT_W, HEAD_H, MONTHS, COLORS, addDays } from "@/components/dashboard/planning/planningConstants";
import PlanningToolbar from "@/components/dashboard/planning/PlanningToolbar";
import PlanningDetailDialog from "@/components/dashboard/planning/PlanningDetailDialog";
import PlanningQuickDialog from "@/components/dashboard/planning/PlanningQuickDialog";
import { PlanningPrintDialog, PlanningSplitDialog } from "@/components/dashboard/planning/PlanningMoveDialogs";
import { PlanningHeaderBar, PlanningSideColumns } from "@/components/dashboard/planning/PlanningFrame";

export default function PlanningGrid({ onSwitchToMap }) {
  const { t, lang } = useLang();
  const [data, setData] = useState(null);
  const [cell, setCell] = useState(22);
  const [scroll, setScroll] = useState({ x: 0, y: 0 });
  const [box, setBox] = useState({ w: 1000, h: 520 });
  const [jump, setJump] = useState("");
  const [detail, setDetail] = useState(null);
  const [quick, setQuick] = useState(null);
  const [guests, setGuests] = useState([]);
  const [gq, setGq] = useState("");
  const [newGuest, setNewGuest] = useState({ first_name: "", last_name: "" });
  const [nights, setNights] = useState(7);
  const [mode, setMode] = useState("reserved");
  const [busy, setBusy] = useState(false);
  const [printOpen, setPrintOpen] = useState(false);
  const [split, setSplit] = useState(null);
  const [chain, setChain] = useState([]);
  const [hist, setHist] = useState(null);
  const [hits, setHits] = useState(null);
  const [found, setFound] = useState(null);

  useEffect(() => {
    if (!detail?.reservation_id) { setChain([]); return; }
    api.get(`/reservations/${detail.reservation_id}/chain`)
      .then(({ data: c }) => setChain(c.length > 1 ? c : []))
      .catch(() => setChain([]));
  }, [detail?.reservation_id]);

  const [period, setPeriod] = useState({ arrival: "", departure: "" });

  useEffect(() => {
    setPeriod({ arrival: detail?.arrival || "", departure: detail?.departure || "" });
  }, [detail?.reservation_id, detail?.arrival, detail?.departure]);

  const savePeriod = async () => {
    try {
      await api.post("/planning/resize", { reservation_id: detail.reservation_id, arrival: period.arrival, departure: period.departure });
      setDetail(null); await load();
      toast.success(`${fmtDate(period.arrival)} → ${fmtDate(period.departure)}`);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const doMerge = async () => {    try {
      await api.post(`/reservations/${detail.reservation_id}/merge`);
      setDetail(null); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const chainCheckin = async () => {    try {
      await api.post(`/reservations/${detail.reservation_id}/chain-checkin`);
      setDetail(null); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const [printMonth, setPrintMonth] = useState(() => new Date().toISOString().slice(0, 7));

  const wrapRef = useRef(null);
  const scRef = useRef(null);
  const panRef = useRef(null);
  const centered = useRef(false);

  const load = useCallback(async () => {
    try {
      setData((await api.get("/planning")).data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    try { setHist((await api.get("/undo/log")).data); } catch (_) { /* geen historiek */ }
  }, []);

  const doUndo = async (kind) => {
    try {
      const { data: r } = await api.post(`/${kind}`);
      toast.success(`${kind === "undo" ? t("undo") : t("redo")}: ${t(`act_${r.action}`) || r.action}`);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const loadGuests = useCallback(async () => {
    if (guests.length) return;
    try { setGuests((await api.get("/guests")).data); } catch (_) { /* lijst blijft leeg */ }
  }, [guests.length]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const measure = () => {
      const el = wrapRef.current;
      if (el) setBox({ w: el.clientWidth - LABEL_W - STAT_W, h: el.clientHeight - HEAD_H });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [data]);

  // Vandaag op ongeveer een derde van het venster
  useEffect(() => {
    if (!data || centered.current || !scRef.current || box.w < 100) return;
    centered.current = true;
    scRef.current.scrollLeft = Math.max(0, data.today_index * cell - box.w / 3);
  }, [data, box.w, cell]);

  const rowH = cell;
  const total = data ? { w: data.days * cell, h: data.rows.length * rowH } : { w: 0, h: 0 };
  const firstCol = Math.max(0, Math.floor(scroll.x / cell) - 2);
  const lastCol = data ? Math.min(data.days, Math.ceil((scroll.x + box.w) / cell) + 2) : 0;
  const firstRow = Math.max(0, Math.floor(scroll.y / rowH) - 2);
  const lastRow = data ? Math.min(data.rows.length, Math.ceil((scroll.y + box.h) / rowH) + 2) : 0;

  const bars = useMemo(() => {
    if (!data) return [];
    return data.rows.flatMap((r, row) => (r.bookings || []).map((b) => ({ ...b, row, label: r.label })));
  }, [data]);

  // Enkel de balken in het zichtbare venster: voorkomt dat de volledige lijst
  // bij elke hertekening (hover, kruisdraad) opnieuw gefilterd wordt.
  const visibleBars = useMemo(
    () => bars.filter((b) => b.row >= firstRow && b.row < lastRow && b.col < lastCol && b.col + b.len > firstCol),
    [bars, firstRow, lastRow, firstCol, lastCol]
  );

  const totals = useMemo(() => {
    if (!data) return { booked: 0, capacity: 0, pct: 0 };
    const booked = data.rows.reduce((a, r) => a + r.booked, 0);
    const capacity = data.rows.length * data.days_in_year;
    return { booked, capacity, pct: capacity ? Math.round(booked * 1000 / capacity) / 10 : 0 };
  }, [data]);

  const crossRowRef = useRef(null);
  const crossColRef = useRef(null);
  const contentRef = useRef(null);
  const keyRef = useRef(null);
  const tipRef = useRef(null);
  const ghostRef = useRef(null);
  const barDragRef = useRef(null);
  const clickTimer = useRef(null);
  const justDropped = useRef(false);

  const cellAt = (e) => {
    const b = contentRef.current.getBoundingClientRect();
    return { col: Math.floor((e.clientX - b.left) / cell), row: Math.floor((e.clientY - b.top) / rowH) };
  };

  const barAt = (row, col) => bars.find((b) => b.row === row && col >= b.col && col < b.col + b.len);

  // Randzone van een balk: links of rechts slepen om te verlengen/inkorten
  const edgeAt = (e, bar) => {
    const b = contentRef.current.getBoundingClientRect();
    const x = e.clientX - b.left;
    const grip = Math.min(8, Math.max(4, cell / 2));
    if (x - bar.col * cell <= grip) return "left";
    if ((bar.col + bar.len) * cell - x <= grip) return "right";
    return null;
  };

  const paintGhost = (d) => {
    const g = ghostRef.current;
    if (!g) return;
    g.style.width = `${d.len * cell - 3}px`;
    g.style.transform = `translate3d(${d.col * cell + 1.5}px,${d.row * rowH + 1.5}px,0)`;
  };

  const startBarDrag = (e, bar, col, mode) => {
    barDragRef.current = { bar, mode, grabOffset: col - bar.col, row: bar.row, col: bar.col, len: bar.len, moved: false };
    const g = ghostRef.current;
    if (g) {
      const c = COLORS[bar.code];
      g.style.background = c.bg;
      g.style.borderColor = c.ring;
      g.style.height = `${rowH - 3}px`;
      g.style.opacity = 1;
    }
    paintGhost(barDragRef.current);
  };

  const dropBar = async (e) => {
    const d = barDragRef.current;
    barDragRef.current = null;
    if (ghostRef.current) ghostRef.current.style.opacity = 0;
    if (!d) return false;
    const unchanged = d.row === d.bar.row && d.col === d.bar.col && d.len === d.bar.len;
    if (!d.moved || unchanged) {
      // Even wachten: een dubbelklik opent het verhuisvenster in plaats van de details
      clearTimeout(clickTimer.current);
      clickTimer.current = setTimeout(
        () => setDetail({ ...d.bar, pitch_label: data.rows[d.bar.row].label, reservation_id: d.bar.id }), 240);
      return true;
    }
    if (d.mode !== "move") {
      const arrival = addDays(data.start, d.col);
      const departure = addDays(data.start, d.col + d.len);
      try {
        await api.post("/planning/resize", { reservation_id: d.bar.id, arrival, departure });
        await load();
        toast.success(`${d.bar.guest_name} · ${fmtDate(arrival)} → ${fmtDate(departure)}`);
      } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
      return true;
    }
    const target = data.rows[d.row];
    try {
      await api.post("/planning/move", { reservation_id: d.bar.id, pitch_label: target.label, arrival: addDays(data.start, d.col) });
      await load();
      toast.success(`${d.bar.guest_name} → ${target.label} · ${fmtDate(addDays(data.start, d.col))}`);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    return true;
  };

  const onGridMove = (e) => {
    if (!data || !contentRef.current) return;
    const { col, row } = cellAt(e);

    // Een balk verslepen
    const drag = barDragRef.current;
    if (drag) {
      drag.moved = true;
      const end = drag.bar.col + drag.bar.len;
      if (drag.mode === "left") {
        const c = Math.max(0, Math.min(end - 1, col));
        drag.col = c; drag.len = end - c; drag.row = drag.bar.row;
      } else if (drag.mode === "right") {
        const e2 = Math.min(data.days, Math.max(drag.bar.col + 1, col + 1));
        drag.col = drag.bar.col; drag.len = e2 - drag.bar.col; drag.row = drag.bar.row;
      } else {
        drag.row = Math.max(0, Math.min(data.rows.length - 1, row));
        drag.col = Math.max(0, Math.min(data.days - drag.bar.len, col - drag.grabOffset));
      }
      paintGhost(drag);
      if (tipRef.current) tipRef.current.style.opacity = 0;
      return;
    }

    // Details bij hover over een balk
    const bar = barAt(row, col);
    if (contentRef.current) contentRef.current.style.cursor = bar && bar.status !== "checked_out" && edgeAt(e, bar) ? "ew-resize" : "";
    if (tipRef.current) {
      if (bar) {
        tipRef.current.innerHTML = bar.code === "X"
          ? `<b>${STATUS.buiten_dienst[lang] || STATUS.buiten_dienst.nl}</b><br>${data.rows[row].label}${bar.oos_note ? `<br>${bar.oos_note}` : ""}${bar.oos_until ? `<br>${t("oos_until")}: ${fmtDate(bar.oos_until)}` : `<br>${t("oos_permanent")}`}`
          : `<b>${bar.guest_name}</b><br>${data.rows[row].label} · ${fmtDate(bar.arrival)} → ${fmtDate(bar.departure)}<br>${bar.persons} ${t("persons").toLowerCase()} · ${bar.status === "checked_out" ? t("checked_out_ok") : bar.status === "checked_in" ? (STATUS.bezet[lang] || "Bezet") : (STATUS.gereserveerd[lang] || "Gereserveerd")}`;
        const wrap = wrapRef.current.getBoundingClientRect();
        tipRef.current.style.left = `${Math.min(e.clientX - wrap.left + 14, wrap.width - 230)}px`;
        tipRef.current.style.top = `${Math.max(8, e.clientY - wrap.top - 62)}px`;
        tipRef.current.style.opacity = 1;
      } else {
        tipRef.current.style.opacity = 0;
      }
    }
    if (crossRowRef.current) {
      crossRowRef.current.style.top = `${row * rowH}px`;
      crossRowRef.current.style.opacity = 1;
    }
    if (crossColRef.current) {
      crossColRef.current.style.left = `${col * cell}px`;
      crossColRef.current.style.opacity = 1;
    }
    if (keyRef.current) {
      keyRef.current.style.transform = `translate3d(${col * cell + 0.75}px,${row * rowH + 0.75}px,0)`;
      keyRef.current.style.opacity = 1;
    }
  };

  const hideCross = () => {
    if (crossRowRef.current) crossRowRef.current.style.opacity = 0;
    if (crossColRef.current) crossColRef.current.style.opacity = 0;
    if (keyRef.current) keyRef.current.style.opacity = 0;
    hidePress();
  };

  const onGridDown = (e) => {
    if (!data || e.button !== 0) return;
    const { col, row } = cellAt(e);
    const bar = barAt(row, col);
    if (bar) {
      e.stopPropagation();
      if (bar.status === "checked_out" || bar.code === "X") return; // historiek en buiten dienst blijven staan
      startBarDrag(e, bar, col, edgeAt(e, bar) || "move");
      return;
    }
    keyRef.current?.classList.add("is-pressed");
  };

  const showPress = () => keyRef.current?.classList.add("is-pressed");
  const hidePress = () => keyRef.current?.classList.remove("is-pressed");

  const onGridClick = (e) => {
    if (!data || panRef.current?.moved || justDropped.current) { justDropped.current = false; return; }
    const { col, row } = cellAt(e);
    if (row < 0 || col < 0 || row >= data.rows.length || col >= data.days) return;
    openCell(row, col);
  };

  const onScroll = (e) => setScroll({ x: e.target.scrollLeft, y: e.target.scrollTop });

  // Dubbelklik op een balk: verhuizen vanaf die dag (het verblijf wordt gesplitst)
  const onGridDoubleClick = (e) => {
    if (!data) return;
    clearTimeout(clickTimer.current);
    const { col, row } = cellAt(e);
    const bar = barAt(row, col);
    if (!bar || bar.status === "checked_out" || bar.code === "X") return;
    const day = addDays(data.start, Math.max(bar.col, Math.min(bar.col + bar.len - 1, col)));
    setSplit({ bar, date: day, pitch: "" });
  };

  const splitCandidates = useMemo(() => {
    if (!data || !split) return [];
    const from = Math.round((new Date(`${split.date}T00:00:00Z`) - new Date(`${data.start}T00:00:00Z`)) / 86400000);
    const till = split.bar.col + split.bar.len;
    return data.rows
      .filter((r, i) => i !== split.bar.row && r.cells.slice(from, till).split("").every((c) => c === "."))
      .map((r) => r.label);
  }, [data, split]);

  const doSplit = async () => {
    setBusy(true);
    try {
      await api.post(`/reservations/${split.bar.id}/move`, { pitch_label: split.pitch, zone: "", move_date: split.date });
      setSplit(null); await load(); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  // Horizontaal slepen met de muis
  const startPan = (e) => {
    if (e.button !== 0) return;
    panRef.current = { x: e.clientX, y: e.clientY, sl: scRef.current.scrollLeft, st: scRef.current.scrollTop, moved: false };
  };
  useEffect(() => {
    const move = (e) => {
      const p = panRef.current;
      if (!p || !scRef.current) return;
      if (Math.abs(e.clientX - p.x) > 4 || Math.abs(e.clientY - p.y) > 4) p.moved = true;
      scRef.current.scrollLeft = p.sl - (e.clientX - p.x);
      scRef.current.scrollTop = p.st - (e.clientY - p.y);
    };
    const up = async (e) => {
      if (barDragRef.current) {
        justDropped.current = true;
        await dropBar(e);
        setTimeout(() => { justDropped.current = false; }, 250);
      }
      hidePress();
      setTimeout(() => { panRef.current = null; }, 0);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  });

  const months = useMemo(() => {
    if (!data) return [];
    const out = [];
    for (let i = 0; i < data.days; i++) {
      const d = new Date(`${addDays(data.start, i)}T00:00:00Z`);
      if (d.getUTCDate() === 1 || i === 0) out.push({ i, label: `${(MONTHS[lang] || MONTHS.en)[d.getUTCMonth()]} ${d.getUTCFullYear()}` });
    }
    return out;
  }, [data, lang]);

  // Zoeken op plaats, gast of nummerplaat
  const doSearch = async () => {
    const s = jump.trim();
    if (!s) return;
    const i = data?.rows.findIndex((r) => r.label.toLowerCase() === s.toLowerCase());
    if (i >= 0) { setHits(null); scRef.current.scrollTop = i * rowH; return; }
    try {
      const { data: rows } = await api.get(`/planning/search?q=${encodeURIComponent(s)}`);
      if (!rows.length) { setHits(null); return toast.error(t("no_data")); }
      if (rows.length === 1) { setHits(null); goToHit(rows[0]); return; }
      setHits(rows);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const goToHit = (h) => {
    setHits(null);
    const row = data.rows.findIndex((r) => r.label === h.pitch_label);
    if (row < 0) return toast.error(t("no_data"));
    const col = Math.round((new Date(`${h.arrival}T00:00:00Z`) - new Date(`${data.start}T00:00:00Z`)) / 86400000);
    scRef.current.scrollTop = Math.max(0, row * rowH - box.h / 3);
    scRef.current.scrollLeft = Math.max(0, col * cell - box.w / 3);
    setFound(h.reservation_id);
    setTimeout(() => setFound(null), 2600);
  };

  const openCell = async (row, col) => {
    if (panRef.current?.moved) return;
    const day = addDays(data.start, col);
    const code = data.rows[row].cells[col];
    if (code === "X") {
      const r = data.rows[row];
      toast.info(`${r.label} · ${STATUS.buiten_dienst[lang] || STATUS.buiten_dienst.nl}${r.oos_note ? ` — ${r.oos_note}` : ""}`);
      return;
    }
    if (code === ".") {
      const cells = data.rows[row].cells;
      let free = 0;
      while (col + free < data.days && cells[col + free] === ".") free += 1;
      const maxNights = col + free >= data.days ? 999 : free;
      setQuick({ pitch: data.rows[row].label, day, maxNights });
      setGq(""); setNewGuest({ first_name: "", last_name: "" }); setNights(Math.min(7, maxNights));
      setMode(day === addDays(data.start, data.today_index) ? "checked_in" : "reserved");
      loadGuests();
      return;
    }
    try {
      const { data: d } = await api.get(`/planning/cell?pitch=${encodeURIComponent(data.rows[row].label)}&day=${day}`);
      setDetail(d);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const createBooking = async (guestId) => {
    setBusy(true);
    try {
      await api.post("/planning/quick-booking", {
        pitch_label: quick.pitch, arrival: quick.day, departure: addDays(quick.day, Math.max(1, Number(nights) || 1)),
        guest_id: guestId || null, mode,
        first_name: guestId ? null : newGuest.first_name, last_name: guestId ? null : newGuest.last_name,
      });
      setQuick(null); await load(); toast.success(t("created"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const visibleGuests = useMemo(() => {
    const s = gq.trim().toLowerCase();
    return guests
      .filter((g) => !s || `${g.first_name} ${g.last_name}`.toLowerCase().includes(s))
      .sort((a, b) => `${a.last_name}${a.first_name}`.localeCompare(`${b.last_name}${b.first_name}`))
      .slice(0, 40);
  }, [guests, gq]);

  return (
    <Panel testid="planning-panel">
      <PlanningToolbar data={data} cell={cell} setCell={setCell} hist={hist} doUndo={doUndo}
        onToday={() => { if (scRef.current) scRef.current.scrollLeft = Math.max(0, data.today_index * cell - box.w / 3); }}
        onPrint={() => setPrintOpen(true)} jump={jump} setJump={setJump} doSearch={doSearch}
        hits={hits} goToHit={goToHit} onSwitchToMap={onSwitchToMap} />

      {!data ? (
        <div className="h-72 flex items-center justify-center text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <>
        <div ref={wrapRef} className="relative mx-5 mb-5 rounded-2xl overflow-hidden"
          style={{
            height: "calc(100vh - 20.5rem)",
            background: "radial-gradient(130% 100% at 15% 0%, #f8fbff 0%, #e6eff9 45%, #d3e2f2 100%)",
            border: "1px solid rgba(148,180,210,.5)",
            boxShadow: "inset 0 1px 0 rgba(255,255,255,.9), inset 0 18px 40px -26px rgba(30,64,120,.35), 0 20px 44px -28px rgba(30,64,120,.35)",
          }} data-testid="planning-grid">

          <PlanningHeaderBar data={data} cell={cell} scrollX={scroll.x} totalW={total.w}
            months={months} firstCol={firstCol} lastCol={lastCol} />
          <PlanningSideColumns data={data} rowH={rowH} scrollY={scroll.y} totalH={total.h}
            firstRow={firstRow} lastRow={lastRow} />

          {/* Raster */}
          <div ref={scRef} onScroll={onScroll} onMouseDown={startPan}
            className="absolute overflow-auto cb-scroll select-none"
            style={{ top: HEAD_H, bottom: 28, left: LABEL_W, right: STAT_W, cursor: panRef.current ? "grabbing" : "grab" }}>
            <div ref={contentRef} onMouseMove={onGridMove} onMouseLeave={hideCross} onMouseUp={hidePress}
              onClick={onGridClick} onDoubleClick={onGridDoubleClick} onMouseDownCapture={onGridDown}
              style={{
                width: total.w, height: total.h, position: "relative",
                backgroundImage: [
                  // fijne rasterlijntjes
                  "linear-gradient(90deg, rgba(120,160,200,.45) 0 1.5px, transparent 1.5px)",
                  "linear-gradient(180deg, rgba(120,160,200,.38) 0 1.5px, transparent 1.5px)",
                  // reliëf per vakje: licht bovenaan, koeler onderaan
                  "linear-gradient(180deg, #ffffff 0%, #ffffff 45%, #e8f0f9 100%)",
                ].join(","),
                backgroundSize: `${cell}px ${rowH}px, ${cell}px ${rowH}px, ${cell}px ${rowH}px`,
              }}>
              <div ref={crossRowRef} className="planning-crosshair-row" style={{ height: rowH }} />
              <div ref={crossColRef} className="planning-crosshair-col" style={{ width: cell }} />

              {/* Boekingsbalken (één element per boeking) */}
              {visibleBars.map((b) => {
                const c = COLORS[b.code];
                const w = b.len * cell - 3;
                return (
                  <div key={b.id} data-bar={b.id} data-testid={`planning-bar-${b.label}-${addDays(data.start, b.col)}`}
                    className="absolute planning-bar flex items-center overflow-hidden"
                    style={{
                      left: b.col * cell + 1.5, top: b.row * rowH + 1.5,
                      width: w, height: rowH - 3,
                      background: c.bg,
                      borderRadius: 7,
                      borderTopLeftRadius: b.clipped_left || b.chain_prev ? 0 : 7, borderBottomLeftRadius: b.clipped_left || b.chain_prev ? 0 : 7,
                      borderTopRightRadius: b.clipped_right || b.chain_next ? 0 : 7, borderBottomRightRadius: b.clipped_right || b.chain_next ? 0 : 7,
                      border: `1px solid ${c.ring}`, borderTopColor: "rgba(255,255,255,.6)",
                      borderLeftStyle: b.clipped_left ? "dashed" : "solid",
                      borderRightStyle: b.clipped_right ? "dashed" : "solid",
                      borderLeftColor: b.chain_prev ? "rgba(255,255,255,.85)" : c.ring,
                      borderRightColor: b.chain_next ? "rgba(255,255,255,.85)" : c.ring,
                      boxShadow: `0 2px 0 rgba(30,64,120,.32), 0 5px 12px -5px ${c.glow}, inset 0 1px 0 rgba(255,255,255,.6)`,
                      ...(c.ribbon ? { outline: "2px solid rgba(76,5,25,.85)", outlineOffset: -3 } : {}),
                      ...(b.id === found ? { outline: "3px solid #0ea5e9", outlineOffset: 2, zIndex: 15 } : {}),
                    }}>
                    {w > 34 && (
                      <span className="pointer-events-none px-1.5 truncate font-semibold text-white leading-none"
                        style={{ fontSize: Math.max(8, Math.min(11, rowH - 12)), textShadow: "0 1px 1px rgba(15,35,70,.55)" }}
                        data-testid={`planning-bar-name-${b.id}`}>{b.last_name || b.guest_name}</span>
                    )}
                    {b.has_rentals && (
                      <span className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 rounded-[2px] bg-white/90 px-[2px] leading-none text-slate-800"
                        style={{ fontSize: Math.max(7, Math.min(9, rowH - 14)) }}
                        title={t("rentals")} data-testid={`planning-bar-rental-${b.id}`}>▣</span>
                    )}
                  </div>
                );
              })}

              {/* Spookbalk tijdens het slepen */}
              <div ref={ghostRef} className="planning-ghost" />

              {/* Toets die de muis volgt: oplichten en indrukken */}
              <div ref={keyRef} className="planning-key" style={{ width: cell - 1.5, height: rowH - 1.5 }} />

              {/* Rode lijn van vandaag */}
              <div className="absolute top-0 bottom-0 z-20 pointer-events-none planning-beam"
                style={{ left: data.today_index * cell - 1, width: 2, background: "linear-gradient(180deg,#fda4af,#fb7185 40%,#be123c)" }} />
            </div>
          </div>

          {/* Details bij hover */}
          <div ref={tipRef} className="planning-tip" />

          {/* Totaal van alle vakken */}
          <div className="absolute bottom-0 left-0 right-0 z-30 flex items-center justify-end gap-3 border-t border-sky-200/80 px-3 py-1.5 text-[11px]" style={{ background: "linear-gradient(0deg,rgba(255,255,255,.98),rgba(226,238,250,.9))", backdropFilter: "blur(14px)" }} data-testid="planning-total">
            <span className="font-bold uppercase tracking-[0.18em] text-slate-500">{t("total")} {data.year}</span>
            <span className="tabular-nums text-slate-500">{totals.booked}/{totals.capacity}</span>
            <span className="font-display font-extrabold text-sm tabular-nums text-sky-700" data-testid="planning-total-pct">{totals.pct}%</span>
          </div>
        </div>
        <div className="mx-5 mb-5 flex flex-wrap items-center gap-x-5 gap-y-1.5 bg-card border border-border rounded-lg px-4 py-2" data-testid="planning-legend">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{t("legend")}</span>
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><i className="h-3 w-3 rounded-[3px] border border-slate-300 bg-card" />{STATUS.vrij[lang] || STATUS.vrij.nl}</span>
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><i className="h-3 w-3 rounded-[3px]" style={{ background: COLORS.R.bg }} />{STATUS.gereserveerd[lang] || STATUS.gereserveerd.nl}</span>
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><i className="h-3 w-3 rounded-[3px]" style={{ background: COLORS.B.bg }} />{STATUS.bezet[lang] || STATUS.bezet.nl}</span>
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><i className="h-3 w-3 rounded-[3px]" style={{ background: COLORS.O.bg }} />{t("history")}</span>
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground"><i className="h-3 w-3 rounded-[3px] ring-2 ring-rose-900" style={{ background: COLORS.b.bg }} />{FLAGS.rood_lintje[lang] || FLAGS.rood_lintje.nl}</span>
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="legend-oos"><i className="h-3 w-3 rounded-[3px]" style={{ background: COLORS.X.bg }} />{STATUS.buiten_dienst[lang] || STATUS.buiten_dienst.nl}</span>
        </div>
        </>
      )}

      <PlanningDetailDialog detail={detail} onClose={() => setDetail(null)} period={period}
        setPeriod={setPeriod} savePeriod={savePeriod} chain={chain} chainCheckin={chainCheckin} doMerge={doMerge} />

      <PlanningQuickDialog quick={quick} onClose={() => setQuick(null)} mode={mode} setMode={setMode}
        nights={nights} setNights={setNights} gq={gq} setGq={setGq} visibleGuests={visibleGuests}
        newGuest={newGuest} setNewGuest={setNewGuest} createBooking={createBooking} busy={busy} />

      <PlanningPrintDialog open={printOpen} setOpen={setPrintOpen} printMonth={printMonth} setPrintMonth={setPrintMonth} />

      <PlanningSplitDialog split={split} setSplit={setSplit} data={data} splitCandidates={splitCandidates}
        doSplit={doSplit} busy={busy} />
    </Panel>
  );
}
